import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./settings.css";

export default function UserSettings() {
  const [user, setUser] = useState(null);

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    darkMode: true,
  });

  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);

        setUser(parsedUser);

        setForm({
          username: parsedUser.username || "",
          email: parsedUser.email || "",
          password: "",
          confirmPassword: "",
          darkMode:
            parsedUser.darkMode !== undefined
              ? parsedUser.darkMode
              : true,
        });
      }
    } catch (error) {
      console.error("Failed to load user settings:", error);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));

    setMessage({
      type: "",
      text: "",
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setMessage({
      type: "",
      text: "",
    });

    if (!form.username.trim()) {
      setMessage({
        type: "error",
        text: "Please enter a username.",
      });
      return;
    }

    if (!form.email.trim()) {
      setMessage({
        type: "error",
        text: "Please enter an email address.",
      });
      return;
    }

    if (form.password && form.password.length < 8) {
      setMessage({
        type: "error",
        text: "Your new password must be at least 8 characters long.",
      });
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessage({
        type: "error",
        text: "Passwords do not match.",
      });
      return;
    }

    try {
      const updatedUser = {
        ...user,
        username: form.username.trim(),
        email: form.email.trim(),
        darkMode: form.darkMode,
      };

      localStorage.setItem("user", JSON.stringify(updatedUser));

      setUser(updatedUser);

      setForm((previous) => ({
        ...previous,
        password: "",
        confirmPassword: "",
      }));

      setMessage({
        type: "success",
        text: "Your settings have been saved successfully.",
      });
    } catch (error) {
      console.error("Failed to save settings:", error);

      setMessage({
        type: "error",
        text: "Unable to save your settings.",
      });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  if (!user) {
    return (
      <div className="settings-container">
        <div className="settings-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Loading your settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-wrapper">

        {/* =================================
            HEADER
        ================================= */}

        <div className="settings-header">
          <div>
            <span className="settings-label">
              ACCOUNT SETTINGS
            </span>

            <h1>Settings</h1>

            <p>
              Manage your Vortex Network account and preferences.
            </p>
          </div>

          <Link to="/profile" className="back-profile-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Back to Profile
          </Link>
        </div>

        {/* =================================
            NOTIFICATION
        ================================= */}

        {message.text && (
          <div
            className={`settings-message ${
              message.type === "success"
                ? "success"
                : "error"
            }`}
          >
            <i
              className={
                message.type === "success"
                  ? "fa-solid fa-circle-check"
                  : "fa-solid fa-circle-exclamation"
              }
            ></i>

            <span>{message.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* =================================
              ACCOUNT
          ================================= */}

          <div className="settings-card">
            <div className="settings-section-header">
              <div>
                <span className="settings-section-label">
                  ACCOUNT
                </span>

                <h2>Account Information</h2>

                <p>
                  Update the information associated with your account.
                </p>
              </div>

              <i className="fa-solid fa-user"></i>
            </div>

            <div className="settings-grid">

              <div className="settings-field">
                <label htmlFor="username">
                  Username
                </label>

                <div className="settings-input-wrapper">
                  <i className="fa-solid fa-user"></i>

                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="Enter your username"
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="settings-field">
                <label htmlFor="email">
                  Email Address
                </label>

                <div className="settings-input-wrapper">
                  <i className="fa-solid fa-envelope"></i>

                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    autoComplete="email"
                  />
                </div>
              </div>

            </div>
          </div>

          {/* =================================
              PASSWORD
          ================================= */}

          <div className="settings-card">
            <div className="settings-section-header">
              <div>
                <span className="settings-section-label">
                  SECURITY
                </span>

                <h2>Password</h2>

                <p>
                  Change your account password.
                </p>
              </div>

              <i className="fa-solid fa-lock"></i>
            </div>

            <div className="settings-grid">

              <div className="settings-field">
                <label htmlFor="password">
                  New Password
                </label>

                <div className="settings-input-wrapper">
                  <i className="fa-solid fa-lock"></i>

                  <input
                    id="password"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Enter a new password"
                    autoComplete="new-password"
                  />
                </div>

                <small>
                  Leave blank if you don't want to change your password.
                </small>
              </div>

              <div className="settings-field">
                <label htmlFor="confirmPassword">
                  Confirm Password
                </label>

                <div className="settings-input-wrapper">
                  <i className="fa-solid fa-shield-halved"></i>

                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                  />
                </div>
              </div>

            </div>
          </div>

          {/* =================================
              APPEARANCE
          ================================= */}

          <div className="settings-card">
            <div className="settings-section-header">
              <div>
                <span className="settings-section-label">
                  PREFERENCES
                </span>

                <h2>Appearance</h2>

                <p>
                  Customize how the Vortex Network website looks.
                </p>
              </div>

              <i className="fa-solid fa-palette"></i>
            </div>

            <label className="settings-toggle">

              <div className="toggle-icon">
                <i className="fa-solid fa-moon"></i>
              </div>

              <div className="toggle-content">
                <strong>Dark Mode</strong>

                <span>
                  Use the dark Vortex Network theme.
                </span>
              </div>

              <div className="toggle-switch">
                <input
                  type="checkbox"
                  name="darkMode"
                  checked={form.darkMode}
                  onChange={handleChange}
                />

                <span className="toggle-slider"></span>
              </div>

            </label>
          </div>

          {/* =================================
              SAVE
          ================================= */}

          <div className="settings-save">
            <button
              type="submit"
              className="save-settings-btn"
            >
              <i className="fa-solid fa-floppy-disk"></i>
              Save Settings
            </button>
          </div>

        </form>

        {/* =================================
            DANGER ZONE
        ================================= */}

        <div className="settings-card danger-zone">

          <div className="settings-section-header">
            <div>
              <span className="settings-section-label danger-label">
                ACCOUNT
              </span>

              <h2>Account Actions</h2>

              <p>
                Manage your current Vortex Network session.
              </p>
            </div>

            <i className="fa-solid fa-triangle-exclamation"></i>
          </div>

          <div className="danger-content">

            <div>
              <strong>Log out of your account</strong>

              <p>
                You will need to sign in again to access your account.
              </p>
            </div>

            <button
              type="button"
              className="settings-logout-btn"
              onClick={handleLogout}
            >
              <i className="fa-solid fa-right-from-bracket"></i>
              Log Out
            </button>

          </div>
        </div>

      </div>
    </div>
  );
}